import { Divider, Stack, StackProps, Typography } from '@mui/material';
import { roundToNearestMinutes } from 'date-fns';
import { groupBy, map, sortBy } from 'lodash';
import { Fragment, ReactNode } from 'react';

import { ProcessingSource } from '@/api/historicalData';
import { Line, LineDataSlice } from '@/charts';
import { DataDetailSectionTimestamp } from '@/components/spotter/historicalDataTab/datumDetail/DataDetailSectionTimestamp';

type Datum = { timestamp: string; processing_source?: ProcessingSource };

export type DataDetailsByTimestampProps<D extends Datum, L extends Line<D>> = {
    selectedData: LineDataSlice<D, L> | null;
    datumRenderer: (datumLine: { datum: D; lines: L[] }) => ReactNode;
};

export const DataDetailsByTimestamp = <D extends Datum, L extends Line<D>>({
    selectedData,
    datumRenderer,
}: DataDetailsByTimestampProps<D, L>) => {
    if (!selectedData) return false;

    const sections = map(
        sortBy(
            Object.entries(
                groupBy(
                    selectedData,
                    ({ datum }) =>
                        `${roundToNearestMinutes(new Date(datum.timestamp)).toISOString()}-${datum.processing_source}`
                )
            ),
            0
        ),
        1
    );
    return (
        <>
            {sections.map((sectionData) => {
                const { timestamp, processing_source } = sectionData[0].datum;
                return (
                    <Stack justifyItems="stretch" key={`${timestamp}-${processing_source}`}>
                        <DataDetailSectionTimestamp timestamp={timestamp} processingSource={processing_source} />
                        <Stack sx={{ justifyItems: 'stretch', m: 1 }}>
                            {sectionData?.map(({ lines, datum }) => (
                                <Fragment key={map(lines, 'id').join(',')}>{datumRenderer({ datum, lines })}</Fragment>
                            ))}
                        </Stack>
                    </Stack>
                );
            })}
        </>
    );
};

export const DataDetailsSection = ({ label, children }: { label?: ReactNode | null; children?: ReactNode }) => (
    <>
        {label && (
            <Typography variant="h6" sx={{ mb: 0.5 }}>
                {label}
            </Typography>
        )}
        {children}
        <Divider sx={{ my: 1, mx: -1, '&:is(hr:last-of-type)': { display: 'none' } }} />
    </>
);

export type DataDetailDatumProps = {
    label?: string;
    icon?: ReactNode;
} & StackProps;

export const DataDetailDatum = ({ children, label, icon, sx, ...props }: DataDetailDatumProps) => (
    <Stack {...props} sx={{ justifyContent: 'start', gap: '2px', ...sx }}>
        <Stack direction="row" sx={{ gap: 0.5, alignItems: 'center' }}>
            {icon}
            <Typography variant="minorLabel">{label}</Typography>
        </Stack>
        <Stack direction="row" sx={{ ml: 2.5, gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
            {children}
        </Stack>
    </Stack>
);
